import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useConnect, useDisconnect, useConnectors } from 'wagmi';
import { motion, AnimatePresence } from 'framer-motion';
import { useWallet } from '@/contexts/WalletContext';
import { useNetworkSwitch } from '@/hooks/useNetworkSwitch';
import { Loading } from '@/components/common';
import bgLogin from '@/assets/login/bg-login.webp';
import seiGlobalIcon from '@/assets/wallets/sei-global.svg';
import metamaskIcon from '@/assets/wallets/metamask.svg';
import compassIcon from '@/assets/wallets/compass.svg';
import type { WalletType } from '@types';

const SeiGlobalIcon = () => (
  <img
    src={seiGlobalIcon}
    alt="Sei Global Wallet"
    className="w-8 h-8 drop-shadow-[0_0_6px_rgba(158,31,25,0.6)]"
  />
);

const MetaMaskIcon = () => (
  <img
    src={metamaskIcon}
    alt="MetaMask"
    className="w-8 h-8 drop-shadow-[0_0_6px_rgba(226,118,37,0.6)]"
  />
);

const CompassIcon = () => (
  <img
    src={compassIcon}
    alt="Compass Wallet"
    className="w-8 h-8 rounded-md drop-shadow-[0_0_6px_rgba(59,158,255,0.6)]"
  />
);

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0,
    },
  },
} as const;

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring' as const,
      stiffness: 300,
      damping: 24,
    },
  },
} as const;

const cardVariants = {
  hidden: { opacity: 0, y: 25, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: 'spring' as const,
      stiffness: 350,
      damping: 25,
    },
  },
} as const;

const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

const getDeepLink = (walletType: WalletType): string | null => {
  switch (walletType) {
    case 'metamask':
      return `https://metamask.app.link/dapp/${window.location.host}${window.location.pathname}`;
    default:
      return null;
  }
};

const getMobileAppStoreLink = (): string => {
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  return isIOS
    ? 'https://apps.apple.com/us/app/compass-wallet-for-sei/id6450257441'
    : 'https://play.google.com/store/apps/details?id=io.leapwallet.compass';
};

export const LoginPage = () => {
  const navigate = useNavigate();
  const [isConnecting, setIsConnecting] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState<WalletType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [connectionStep, setConnectionStep] = useState<
    'connect' | 'switch' | 'sign'
  >('connect');

  const connectors = useConnectors();
  const { connect } = useConnect();
  const { disconnectAsync } = useDisconnect();
  const { login, isAuthenticated, isLoading } = useWallet();
  const {
    requiredNetwork,
    switchToCorrectNetwork,
    error: networkError,
  } = useNetworkSwitch();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/game');
    }
  }, [isAuthenticated, navigate]);

  const handleWalletSelect = async (walletType: WalletType) => {
    if (isMobileDevice()) {
      const deepLink = getDeepLink(walletType);
      if (deepLink) {
        window.location.href = deepLink;
        return;
      }

      if (walletType === 'compass') {
        const confirmed = window.confirm(
          'Please open this site in Compass Wallet browser.\n\nTap OK to download Compass Wallet.'
        );
        if (confirmed) {
          window.location.href = getMobileAppStoreLink();
        }
        return;
      }
    }

    setSelectedWallet(walletType);
    setError(null);
    setIsConnecting(true);
    setConnectionStep('connect');

    try {
      await disconnectAsync().catch(() => {});

      let connector;

      switch (walletType) {
        case 'sei-global':
          connector = connectors.find((c) => {
            const nameMatch = c.name.toLowerCase().includes('sei');
            const idMatch = c.id.toLowerCase().includes('sei');
            const typeMatch = c.type === 'injected';
            return (nameMatch || idMatch) && typeMatch;
          });
          if (!connector) throw new Error('Sei Global Wallet not detected');
          break;

        case 'metamask':
          connector = connectors.find(
            (c) =>
              c.name.toLowerCase().includes('metamask') ||
              c.id.toLowerCase().includes('metamask')
          );
          if (!connector) throw new Error('MetaMask not detected');
          break;

        case 'compass':
          connector = connectors.find(
            (c) =>
              c.name.toLowerCase().includes('compass') ||
              c.id.toLowerCase().includes('compass')
          );
          if (!connector) throw new Error('Compass Wallet not detected');
          break;
      }

      const result = await new Promise<{
        accounts: readonly string[];
        chainId: number;
      }>((resolve, reject) => {
        connect(
          { connector },
          {
            onSuccess: (data) => resolve(data),
            onError: (err) => reject(err),
          }
        );
      });

      setConnectionStep('switch');
      const switched = await switchToCorrectNetwork();
      if (!switched) {
        throw new Error(`Please switch to ${requiredNetwork} to continue`);
      }

      setConnectionStep('sign');
      await login(walletType, result.accounts[0]);
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to connect wallet';
      setError(errorMessage);
    } finally {
      setIsConnecting(false);
      setSelectedWallet(null);
      setConnectionStep('connect');
    }
  };

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [error]);

  useEffect(() => {
    if (networkError) {
      setError(networkError);
    }
  }, [networkError]);

  // Wallet configurations - unified purple theme with brand icons
  const wallets = [
    {
      type: 'sei-global' as WalletType,
      name: 'Sei Global Wallet',
      description: 'Social login with Google, Twitter',
      Icon: SeiGlobalIcon,
    },
    {
      type: 'metamask' as WalletType,
      name: 'MetaMask',
      description: 'Popular crypto wallet',
      Icon: MetaMaskIcon,
    },
    {
      type: 'compass' as WalletType,
      name: 'Compass Wallet',
      description: 'Sei native wallet with EVM',
      Icon: CompassIcon,
    },
  ];

  const getConnectionStepText = () => {
    switch (connectionStep) {
      case 'connect':
        return 'Connecting...';
      case 'switch':
        return `Switching to ${requiredNetwork}...`;
      case 'sign':
        return 'Sign message...';
      default:
        return 'Connecting...';
    }
  };

  return (
    <div
      className="w-full h-full relative overflow-hidden"
      style={{
        backgroundImage: `url(${bgLogin})`,
        backgroundSize: '100% 100%',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* Gradient overlay - stronger at bottom for card visibility */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent pointer-events-none" />

      {/* Loading Overlay */}
      {isLoading && (
        <>
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100]" />
          <div className="fixed inset-0 z-[101] flex items-center justify-center">
            <Loading text="Entering the Sanctum" size="lg" />
          </div>
        </>
      )}

      {/* Main Content */}
      <div className="relative z-10 h-full flex flex-col items-center justify-end pb-8 px-4">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="w-full max-w-sm"
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="text-center mb-5">
            <h2
              className="text-2xl font-lilita tracking-wide"
              style={{
                color: '#FFD700',
                textShadow: `
                  -1px -1px 0 #000, 1px -1px 0 #000,
                  -1px 1px 0 #000, 1px 1px 0 #000,
                  0 0 20px rgba(255, 165, 0, 0.6),
                  0 0 40px rgba(255, 165, 0, 0.4)
                `,
              }}
            >
              Connect Wallet
            </h2>
            <p
              className="text-sm mt-1 font-lilita"
              style={{
                color: '#fcd34d',
                textShadow: `
                  -0.5px -0.5px 0 #000, 0.5px -0.5px 0 #000,
                  -0.5px 0.5px 0 #000, 0.5px 0.5px 0 #000,
                  0 0 10px rgba(251, 191, 36, 0.4)
                `,
              }}
            >
              Choose your gateway to the Sanctum
            </p>
          </motion.div>

          {/* Network Badge */}
          <motion.div
            variants={itemVariants}
            className="flex justify-center mb-4"
          >
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full"
              style={{
                background: 'rgba(16, 185, 129, 0.12)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                border: '1px solid rgba(16, 185, 129, 0.25)',
                boxShadow:
                  '0 0 20px rgba(16, 185, 129, 0.15), inset 0 1px 0 rgba(255,255,255,0.05)',
              }}
            >
              <div
                className="w-2 h-2 rounded-full animate-pulse"
                style={{
                  background: '#34d399',
                  boxShadow:
                    '0 0 8px rgba(52, 211, 153, 0.8), 0 0 16px rgba(52, 211, 153, 0.4)',
                }}
              />
              <span
                className="text-xs font-lilita"
                style={{
                  color: 'rgba(167, 243, 208, 0.95)',
                  textShadow: '0 0 10px rgba(52, 211, 153, 0.5)',
                }}
              >
                {requiredNetwork}
              </span>
            </div>
          </motion.div>

          {/* Wallet Cards */}
          <div className="space-y-3">
            {wallets.map((wallet, index) => {
              const glowColors: Record<
                WalletType,
                { primary: string; secondary: string; rgb: string }
              > = {
                'sei-global': {
                  primary: '#ff4444',
                  secondary: '#ff6b6b',
                  rgb: '255, 68, 68',
                },
                metamask: {
                  primary: '#f6851b',
                  secondary: '#ffb347',
                  rgb: '246, 133, 27',
                },
                compass: {
                  primary: '#3b9eff',
                  secondary: '#60b8ff',
                  rgb: '59, 158, 255',
                },
              };
              const glow = glowColors[wallet.type];

              return (
                <motion.button
                  key={wallet.type}
                  variants={cardVariants}
                  custom={index}
                  whileHover={{
                    scale: 1.02,
                    y: -2,
                  }}
                  whileTap={{ scale: 0.98 }}
                  transition={{
                    type: 'spring',
                    stiffness: 400,
                    damping: 25,
                  }}
                  onClick={() => handleWalletSelect(wallet.type)}
                  disabled={isConnecting}
                  className="group relative w-full p-4 rounded-2xl text-left disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden"
                  style={{
                    background:
                      'linear-gradient(135deg, rgba(45, 35, 70, 0.85) 0%, rgba(30, 25, 55, 0.9) 100%)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    boxShadow: `
                      0 8px 32px rgba(0, 0, 0, 0.5),
                      0 0 0 1px rgba(255, 255, 255, 0.05),
                      inset 0 1px 0 rgba(255, 255, 255, 0.1),
                      inset 0 0 20px rgba(139, 92, 246, 0.05)
                    `,
                  }}
                >
                  <div
                    className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                    style={{
                      background: `linear-gradient(135deg, ${glow.primary}40 0%, transparent 50%, ${glow.secondary}40 100%)`,
                      padding: '1px',
                      mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                      maskComposite: 'exclude',
                      WebkitMaskComposite: 'xor',
                    }}
                  />

                  <div
                    className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                    style={{
                      boxShadow: `
                        0 0 30px rgba(${glow.rgb}, 0.15),
                        0 0 60px rgba(${glow.rgb}, 0.08),
                        inset 0 0 30px rgba(${glow.rgb}, 0.03)
                      `,
                    }}
                  />

                  <div
                    className="absolute top-0 left-4 right-4 h-px opacity-50 group-hover:opacity-70 transition-opacity"
                    style={{
                      background:
                        'linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)',
                    }}
                  />

                  <div className="relative flex items-center gap-4">
                    <div
                      className="shrink-0 w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300"
                      style={{
                        background: 'rgba(255, 255, 255, 0.08)',
                        border: '1px solid rgba(255, 255, 255, 0.12)',
                        boxShadow: `
                          inset 0 1px 0 rgba(255,255,255,0.1),
                          0 4px 12px rgba(0,0,0,0.3)
                        `,
                      }}
                    >
                      <div className="group-hover:scale-110 transition-transform duration-300">
                        <wallet.Icon />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="grow min-w-0">
                      <div
                        className="font-lilita text-base truncate"
                        style={{
                          color: 'rgba(255, 255, 255, 0.95)',
                          textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                        }}
                      >
                        {wallet.name}
                      </div>
                      <div
                        className="text-xs truncate mt-0.5 transition-colors duration-300"
                        style={{
                          color:
                            selectedWallet === wallet.type && isConnecting
                              ? glow.secondary
                              : 'rgba(255, 255, 255, 0.5)',
                          textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                        }}
                      >
                        {selectedWallet === wallet.type && isConnecting
                          ? getConnectionStepText()
                          : wallet.description}
                      </div>
                    </div>

                    {/* Arrow / Spinner */}
                    <div className="shrink-0">
                      {selectedWallet === wallet.type && isConnecting ? (
                        <div
                          className="w-5 h-5 rounded-full animate-spin"
                          style={{
                            border: `2px solid rgba(${glow.rgb}, 0.2)`,
                            borderTopColor: glow.primary,
                            boxShadow: `0 0 12px rgba(${glow.rgb}, 0.4)`,
                          }}
                        />
                      ) : (
                        <motion.div
                          className="w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300"
                          style={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.15)',
                          }}
                          whileHover={{ x: 3 }}
                        >
                          <svg
                            className="w-4 h-4 transition-colors duration-300"
                            style={{ color: 'rgba(255, 255, 255, 0.75)' }}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2.5}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </motion.div>
                      )}
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </div>

          {/* Error Message */}
          <AnimatePresence mode="popLayout">
            {error && (
              <motion.div
                layout
                initial={{ opacity: 0, height: 0, marginTop: 0 }}
                animate={{ opacity: 1, height: 'auto', marginTop: 12 }}
                exit={{ opacity: 0, height: 0, marginTop: 0 }}
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                className="overflow-hidden"
              >
                <div
                  className="p-3 rounded-xl flex items-start gap-2 relative"
                  style={{
                    background: 'rgba(239, 68, 68, 0.12)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    border: '1px solid rgba(239, 68, 68, 0.25)',
                    boxShadow:
                      '0 0 20px rgba(239, 68, 68, 0.15), inset 0 1px 0 rgba(255,255,255,0.03)',
                  }}
                >
                  <svg
                    className="w-4 h-4 shrink-0 mt-0.5"
                    style={{
                      color: 'rgba(252, 165, 165, 0.9)',
                      filter: 'drop-shadow(0 0 6px rgba(248, 113, 113, 0.6))',
                    }}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <p
                    className="text-xs pr-6 font-medium"
                    style={{
                      color: 'rgba(254, 202, 202, 0.95)',
                      textShadow: '0 0 8px rgba(248, 113, 113, 0.3)',
                    }}
                  >
                    {error}
                  </p>
                  <motion.button
                    onClick={() => setError(null)}
                    className="absolute top-2 right-2 p-1 rounded-full transition-colors"
                    style={{
                      color: 'rgba(252, 165, 165, 0.7)',
                    }}
                    whileHover={{
                      scale: 1.1,
                      backgroundColor: 'rgba(239, 68, 68, 0.2)',
                    }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Legal Links */}
          <motion.div
            variants={itemVariants}
            className="flex items-center justify-center gap-3 mt-5"
          >
            <Link
              to="/privacy-policy"
              className="text-xs font-medium transition-colors duration-200"
              style={{
                color: 'rgba(255, 255, 255, 0.45)',
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)')
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.color = 'rgba(255, 255, 255, 0.45)')
              }
            >
              Privacy Policy
            </Link>
            <span style={{ color: 'rgba(255, 255, 255, 0.2)' }}>•</span>
            <Link
              to="/terms-of-service"
              className="text-xs font-medium transition-colors duration-200"
              style={{
                color: 'rgba(255, 255, 255, 0.45)',
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)')
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.color = 'rgba(255, 255, 255, 0.45)')
              }
            >
              Terms of Service
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};
